import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { createDashboard, updateDashboard } from '../store/dashboardSlice';
import { showSnackbar } from '../store/snackbarSlice';

function CreateDashboardModal({ 
  open, 
  onClose, 
  dashboardToEdit = null, // Pass existing dashboard for edit mode
  onSuccess = null // Optional callback for when operation is successful
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  // Determine if we're in edit mode
  const isEditMode = Boolean(dashboardToEdit);

  // Update form data when dashboardToEdit changes
  useEffect(() => {
    if (dashboardToEdit) {
      setFormData({
        title: dashboardToEdit.title || '',
        description: dashboardToEdit.description || ''
      });
    } else {
      // Reset form data when not in edit mode
      setFormData({
        title: '',
        description: ''
      });
    }
  }, [dashboardToEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: ''
    });
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      return; // Don't submit if title is empty
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Update existing dashboard
        await dispatch(updateDashboard({
          dashboard_id: dashboardToEdit.dashboard_id,
          title: formData.title.trim(),
          description: formData.description.trim()
        })).unwrap();
        
        // Show success message
        dispatch(showSnackbar({
          message: 'Dashboard updated successfully',
          severity: 'success'
        }));
      } else {
        // Create new dashboard
        const result = await dispatch(createDashboard({
          user_id: user?.user?.id,
          title: formData.title.trim(),
          description: formData.description.trim()
        })).unwrap();
        
        // Show success message
        dispatch(showSnackbar({
          message: 'Dashboard created successfully',
          severity: 'success'
        }));
        
        // Pass the newly created dashboard ID to the onSuccess callback
        if (onSuccess && result && result.dashboard_id) {
          onSuccess(result.dashboard_id);
        }
      }
      
      if (onSuccess && !isEditMode) {
        onSuccess();
      }
      
      handleClose();
    } catch (error) {
      console.error('Error saving dashboard:', error);
      
      // Show error message
      dispatch(showSnackbar({
        message: `Error ${isEditMode ? 'updating' : 'creating'} dashboard: ${error.message || 'Unknown error'}`,
        severity: 'error'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key for form submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !isSubmitting && handleClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dashboard-dialog-title"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)', // lighter dark
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          background: 'rgb(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '2px solid rgba(200, 200, 200, 0.3)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
          maxWidth: '500px',
          overflowX: 'hidden',
        }
      }}
    >
      <DialogTitle id="dashboard-dialog-title" sx={{ marginTop: '10px', marginLeft: '3px', padding: '10px 24px' }}>
        {isEditMode ? 'Edit Dashboard' : 'Create New Dashboard'}
      </DialogTitle>
      
      <DialogContent sx={{ 
        overflowX: 'hidden',
        '& .MuiTextField-root': {
          width: '100%',
          '& .MuiInputBase-root': {
            width: '100%',
            '& textarea': {
              width: '100%',
              overflowX: 'hidden',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }
          }
        }
      }}>
        <TextField
          autoFocus
          margin="dense"
          name="title"
          label="Dashboard Title"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.title}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
          }}
          required
        />
        
        <TextField
          margin="dense"
          name="description"
          label="Description (Optional)"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.description}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          multiline
          rows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              '& fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
              },
              '&.Mui-focused': {
                boxShadow: 'none', // remove any default glow
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
              },
              overflowX: 'hidden'
            },
            '& textarea': {
              color: '#333',
              overflowX: 'hidden !important',
              whiteSpace: 'pre-wrap !important',
              wordWrap: 'break-word !important'
            },
            '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ marginBottom: '10px', marginRight: '17px', overflowX: 'hidden' }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(150, 150, 150, 0.3)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(150, 150, 150, 0.4)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting || !formData.title.trim()}
          sx={{
            borderRadius: '12px',
            background: 'rgba(18, 157, 232, 0.1)',
            boxShadow: 'none',
            border: '1px solid rgba(18, 157, 232, 0.2)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(18, 157, 232, 0.2)',
                borderColor: 'rgba(18, 157, 232, 0.5)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateDashboardModal; 